/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-console */
/* eslint-disable security/detect-object-injection */
/* eslint-disable import/no-unresolved */
import {
  handelIndianCurrencyChange,
  handleUppercaseChange,
  onChangeIndianCurrency
} from "src/helpers/FormDetails";
import { findKeysByValue, OPTIONS } from "src/helpers/FormDetails/ProposalDetails/resources";
import CustomDatePicker from "src/shared/CustomDatePicker";
import CustomRadioButton from "src/shared/CustomRadioButton";
import CustomSelect from "src/shared/CustomSelect";
import CustomTextField from "src/shared/CustomTextField";
import { useAppSelector } from "src/utils/hooks/useAppSelector";
import { getAllPartners } from "src/services/Master";
import { useEffect, useLayoutEffect, useState } from "react";
import { FormikProps } from "formik";
import {
  CreateLeadsDetails,
  uploadFilesMandatoryFor
} from "src/helpers/FormDetails/CreateDetails/interfaces";
import StorageService from "src/utils/Storage/storageService";
import { Button } from "@mui/material";
import CustomDocumentUpload from "src/shared/CustomDocumentUpload";
import CustomErrorText from "src/shared/CustomErrorText";

interface Props {
  formik: FormikProps<CreateLeadsDetails>;
  genderDataList: string[];
}

function CreateNewLead({ formik, genderDataList }: Props) {
  const { errors, handleBlur, setFieldTouched, touched, values, handleChange, setFieldValue } =
    formik;
  const [allPartners, setAllPartners] = useState<{ partnerCode: string; channel: string }[]>([]);
  const { product: productData = {} } = useAppSelector((state) => state.masterdata_reducer);
  const partnerProduct = useAppSelector((state) => state.masterdata_reducer.partnerProduct) || {};
  const channel = useAppSelector((state) => state.masterdata_reducer.channel) || {};
  const loanTerm = useAppSelector((state) => state.masterdata_reducer?.term) || {};
  const minLoanTerm = Math.min(...Object.keys(loanTerm || {}).map(Number));
  const premiumPaymentTerm =
    useAppSelector((state) => state.masterdata_reducer?.premiumPaymentTerm) || {};
  const [policyTermOptions, setPolicyTermOptions] = useState<string[]>();
  const loggedInUser = StorageService.getFromLocalStorage("user");
  useLayoutEffect(() => {
    const options = Array.from(
      { length: +(values.loanTerm || 30) - (minLoanTerm - 1) },
      (_, index) => (index + minLoanTerm).toString()
    );
    setPolicyTermOptions(options);
  }, [values?.loanTerm]);
  const userPartnerCode = StorageService.getFromLocalStorage("user")?.partnerCode;
  const [toggleUploadFile, setToggleUploadFile] = useState(false);

  const partnerProductOptions = productData
    ? Object.keys(productData).reduce(
        (acc, productId) => {
          const productIdNumber = Number(productId);
          acc[productId] = partnerProduct
            ? Object.keys(partnerProduct)
                .filter((partner) => {
                  const productIds = partnerProduct[partner];
                  return Array.isArray(productIds) && productIds.includes(productIdNumber);
                })
                .map((partner) => partner.toUpperCase())
            : [];
          return acc;
        },
        {} as Record<string, string[]>
      )
    : {};

  const filterPartnerProductOptions = () => {
    if (!allPartners || !Array.isArray(allPartners)) return {};
    if (!partnerProductOptions || typeof partnerProductOptions !== "object") return {};

    const validPartners = allPartners
      .filter((partner) => partner?.channel === values?.channelCode)
      .map((partner) => partner?.partnerCode?.toUpperCase())
      .filter(Boolean);

    const filteredOptions = Object.fromEntries(
      Object.entries(partnerProductOptions)
        .map(([productCode, partners]) => [
          productCode,
          (partners || []).filter((partner) => validPartners.includes(partner))
        ])
        .filter(([_, partners]) => partners.length > 0)
    );

    return filteredOptions as Record<string, string[]>;
  };

  useEffect(() => {
    const fetchPartners = async () => {
      try {
        const response = await getAllPartners();
        setAllPartners(response);
      } catch (error) {
        console.error("Error fetching partners:", error);
      }
    };
    fetchPartners();
  }, []);

  useEffect(() => {
    if (values?.channel) setFieldValue("channelCode", findKeysByValue(channel, values?.channel));
  }, [values?.channel, values?.productId]);

  useEffect(() => {
    setFieldValue(
      "premiumPaymentTermCode",
      findKeysByValue(premiumPaymentTerm, String(values?.premiumPaymentTerm || ""))
    );
  }, [values.premiumPaymentTerm]);

  useEffect(() => {
    if (userPartnerCode) {
      setFieldValue("productId", partnerProduct[userPartnerCode]?.[0] || "");
    }
  }, [values?.productId, userPartnerCode]);

  const handleUploadFilesButton = () => {
    setToggleUploadFile(true);
  };

  return (
    <>
      {toggleUploadFile && (
        <CustomDocumentUpload
          multipleUploads
          formik={formik}
          onSave={() => {
            setToggleUploadFile(false);
          }}
          onClose={() => {
            setToggleUploadFile(false);
          }}
        />
      )}
      <p className="font-montserrat text-black text-base font-bold mb-6 flex">
        Application Details
      </p>
      <div className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-10 gap-y-6">
          <CustomSelect
            requiredStar
            label="Product"
            options={Object.values(productData)}
            onChange={(e: any) => {
              const productId = Object.keys(productData).find(
                (pId: string) => productData[pId] === e.target.value
              );
              setFieldValue("productId", productId);
              setFieldValue("partnerId", "");
              setFieldValue("loanAmount", "");
              setFieldValue("loanTerm", "");
              setFieldValue("sumAssured", "");
              setFieldValue("policyTerm", "");
              setFieldValue("premiumPaymentTerm", "");
              setFieldValue("loanInterestRate", "");
            }}
            setFieldTouched={setFieldTouched}
            name="productId"
            value={productData?.[values?.productId!] || ""}
            error={errors.productId}
            touched={touched.productId}
            disabledField={!!userPartnerCode}
          />
          <CustomTextField
            requiredStar
            label="Application Number"
            onChange={handleChange}
            handleBlur={handleBlur}
            value={values.applicationNumber}
            name="applicationNumber"
            error={errors.applicationNumber}
            touched={touched.applicationNumber}
          />
          <CustomSelect
            requiredStar
            label="Type Of Loan"
            options={OPTIONS.typeOfLoan}
            onChange={handleChange}
            setFieldTouched={setFieldTouched}
            name="loanType"
            value={values.loanType}
            error={errors.loanType}
            touched={touched.loanType}
          />
          <CustomTextField
            requiredStar
            label="Loan Account No."
            onChange={handleChange}
            handleBlur={handleBlur}
            value={values.loanAccountNo}
            name="loanAccountNo"
            error={errors.loanAccountNo}
            touched={touched.loanAccountNo!}
          />
        </div>
      </div>
      <p className="font-montserrat text-black text-base font-bold mb-6 mt-6 flex">
        Channel Details
      </p>
      <div className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-10 gap-y-6">
          <CustomSelect
            requiredStar
            label="Channel"
            options={Object.values(channel) || []}
            onChange={(e) => {
              handleChange(e);
              setFieldValue("bankVertical", "");
            }}
            setFieldTouched={setFieldTouched}
            name="channel"
            value={values.channel}
            error={errors.channel}
            touched={touched.channel}
          />
          <CustomSelect
            requiredStar
            disabledField={!values.productId || !values.channel}
            label="Distributor Name"
            options={filterPartnerProductOptions()[values.productId!] || []}
            onChange={handleChange}
            setFieldTouched={setFieldTouched}
            name="partnerId"
            value={values.partnerId}
            error={errors.partnerId}
            touched={touched.partnerId}
          />
          <CustomTextField
            requiredStar={values.channelCode === "BA"}
            label="Bank Vertical"
            onChange={handleChange}
            handleBlur={handleBlur}
            value={values.bankVertical}
            name="bankVertical"
            error={errors.bankVertical}
            touched={touched.bankVertical!}
            disabledField={values.channelCode !== "BA"}
          />
          <CustomTextField
            requiredStar
            label="Sol ID"
            onChange={handleChange}
            handleBlur={handleBlur}
            value={values.solId}
            name="solId"
            error={errors.solId}
            touched={touched.solId!}
          />
          <CustomTextField
            requiredStar
            label="LG Code"
            onChange={handleChange}
            handleBlur={handleBlur}
            value={values.lgCode}
            name="lgCode"
            error={errors.lgCode}
            touched={touched.lgCode!}
          />
        </div>
      </div>
      <p className="font-montserrat text-black text-base font-bold mb-6 mt-6 flex">
        Customer Details
      </p>
      <div className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-10 gap-y-6">
          <CustomTextField
            requiredStar
            label="First Name"
            onChange={handleChange}
            handleBlur={handleBlur}
            value={values.firstName}
            name="firstName"
            error={errors.firstName}
            touched={touched.firstName}
          />
          <CustomTextField
            label="Middle Name"
            onChange={handleChange}
            handleBlur={handleBlur}
            value={values?.middleName!}
            name="middleName"
            error={errors.middleName}
            touched={touched.middleName}
          />
          <CustomTextField
            label="Last Name"
            onChange={handleChange}
            handleBlur={handleBlur}
            value={values.lastName}
            name="lastName"
            error={errors.lastName}
            touched={touched.lastName}
          />
          <CustomDatePicker
            requiredStar
            disableFuture
            label="DOB"
            setFieldValue={setFieldValue}
            name="customerDOB"
            value={values.customerDOB}
            error={errors?.customerDOB}
            touched={touched.customerDOB}
            setFieldTouched={setFieldTouched}
          />
          <CustomTextField
            requiredStar
            label="Mobile Number"
            onChange={handleChange}
            handleBlur={handleBlur}
            value={values?.customerMobileNumber!}
            name="customerMobileNumber"
            error={errors?.customerMobileNumber}
            touched={touched?.customerMobileNumber}
          />
          <CustomTextField
            label="PAN No."
            onChange={(e) => {
              handleUppercaseChange(e, handleChange);
            }}
            handleBlur={handleBlur}
            name="customerPAN"
            value={values.customerPAN}
            error={errors.customerPAN}
            touched={touched.customerPAN}
          />
          <CustomTextField
            label="Email"
            onChange={handleChange}
            handleBlur={handleBlur}
            value={values.customerEmail}
            name="customerEmail"
            error={errors.customerEmail}
            touched={touched.customerEmail}
          />
          <CustomDatePicker
            requiredStar
            label="Application Signed Date"
            setFieldValue={setFieldValue}
            name="applicationSignedDate"
            value={values.applicationSignedDate}
            error={errors?.applicationSignedDate}
            touched={touched.applicationSignedDate}
            setFieldTouched={setFieldTouched}
          />
          <CustomRadioButton
            requiredStar
            className={`${
              values.title === "Others"
                ? "col-span-1 sm:col-span-1 md:col-span-2 lg:col-span-3"
                : "col-span-1 sm:col-span-1 md:col-span-2 lg:col-span-3"
            }`}
            name="customerGender"
            label="Gender"
            options={genderDataList}
            setFieldValue={setFieldValue}
            setFieldTouched={setFieldTouched}
            error={errors.customerGender}
            touched={touched.customerGender}
            defaultValue={values.customerGender}
          />
        </div>
      </div>
      {values.productId && values.partnerId ? (
        <>
          <p className="font-montserrat text-black text-base font-bold mb-6 mt-6 flex">
            Plan Details
          </p>
          <div className="space-y-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-10 gap-y-6">
              <CustomTextField
                requiredStar
                label="Loan Amount"
                onChange={(event) => handelIndianCurrencyChange(event, { setFieldValue })}
                handleBlur={handleBlur}
                name="loanAmount"
                value={onChangeIndianCurrency(values?.loanAmount!)}
                error={errors.loanAmount}
                touched={touched.loanAmount}
              />
              <CustomSelect
                requiredStar
                label="Loan Term (Months)"
                options={Object.values(loanTerm)}
                onChange={handleChange}
                setFieldTouched={setFieldTouched}
                name="loanTerm"
                value={values.loanTerm}
                error={errors.loanTerm}
                touched={touched.loanTerm}
              />
              <CustomTextField
                requiredStar
                label="Sum Assured"
                onChange={(event) => handelIndianCurrencyChange(event, { setFieldValue })}
                handleBlur={handleBlur}
                name="sumAssured"
                value={onChangeIndianCurrency(values?.sumAssured!)}
                error={errors.sumAssured}
                touched={touched.sumAssured}
              />
              <CustomSelect
                requiredStar
                label="Policy Term"
                options={policyTermOptions!}
                onChange={handleChange}
                setFieldTouched={setFieldTouched}
                name="policyTerm"
                value={values.policyTerm}
                error={errors.policyTerm}
                touched={touched.policyTerm}
              />
              <CustomSelect
                requiredStar
                label="Premium Payment Term"
                options={Object.values(premiumPaymentTerm)}
                onChange={handleChange}
                setFieldTouched={setFieldTouched}
                name="premiumPaymentTerm"
                value={String(values.premiumPaymentTerm || "")}
                error={errors.premiumPaymentTerm}
                touched={touched.premiumPaymentTerm}
              />
              <CustomTextField
                label="Loan Interest Rate %"
                onChange={handleChange}
                handleBlur={handleBlur}
                value={values.loanInterestRate}
                name="loanInterestRate"
                error={errors.loanInterestRate}
                touched={touched.loanInterestRate!}
              />
            </div>
          </div>
        </>
      ) : null}
      {uploadFilesMandatoryFor(loggedInUser?.role) && (
        <>
          <Button
            variant="contained"
            classes={{ contained: "!rounded-lg !h-12 !mt-6 !uppercase !text-sm" }}
            className="!uppercase m-auto"
            onClick={handleUploadFilesButton}
          >
            upload files
          </Button>
          {formik?.touched?.files && formik?.errors?.files?.length && (
            <CustomErrorText text="Uploading file is mandatory" type="error" className="!mb-6" />
          )}
        </>
      )}
    </>
  );
}

export default CreateNewLead;
